import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Filter, Unlink, Loader2, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import caregiverService from '../../services/caregiverService';
import CaregiverStatusBadge, { STATUS_VALUES } from './statusBadge';
import AddCaregiverModal from './AddCaregiverModal';
import DeactivateLinkConfirm from './DeactivateLinkConfirm';
import CaregiverDetail from './CaregiverDetail';

const FILTER_OPTIONS = ['All', ...STATUS_VALUES];

const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (_) {
    return '';
  }
};

/**
 * Caregivers tab on Participant Details (FECOA-1321).
 * Includes:
 * - Search by name/ID/email (FECOA-1370, client-side over the loaded list)
 * - Status filter dropdown (FECOA-1370)
 * - Status badges per row (FECOA-1375)
 * - "+ Add Caregiver" entry into the search-first modal (FECOA-1369 + FECOA-1362)
 * - Per-row unlink shortcut (FECOA-1374)
 */
const CaregiversTab = ({ participantId, participantLabel, siteId, studyId, studyCode, onCaregiversChanged }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [allowCaregivers, setAllowCaregivers] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // The detail view renders inline when ?selected_caregiver=<id> is set.
  // Keeping it on the same route prevents a full ParticipantDetails remount
  // and makes "Back to Caregivers List" feel instant.
  const selectedCaregiverId = searchParams.get('selected_caregiver') || null;

  // FECOA-1319 hand-off: Add Participant flow may navigate here with
  // `?addCaregiver=1` — auto-open the modal so the SC continues straight in.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('addCaregiver') === '1') {
      setAddOpen(true);
      // Strip the query param so refreshes don't re-trigger
      const cleanSearch = new URLSearchParams(location.search);
      cleanSearch.delete('addCaregiver');
      navigate(
        { pathname: location.pathname, search: cleanSearch.toString(), hash: location.hash },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCaregivers = async () => {
    try {
      setLoading(true);
      const data = await caregiverService.listForParticipant(participantId);
      setAllowCaregivers(!!data.allow_caregivers);
      setRows(data.caregivers || []);
    } catch (err) {
      console.error('Error loading caregivers:', err);
      toast.error('Failed to load caregivers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (participantId) loadCaregivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== 'All' && row.status !== statusFilter) return false;
      if (!q) return true;
      const cg = row.caregiver || {};
      return (
        (cg.full_name && cg.full_name.toLowerCase().includes(q)) ||
        (cg.first_name && cg.first_name.toLowerCase().includes(q)) ||
        (cg.last_name && cg.last_name.toLowerCase().includes(q)) ||
        (cg.email && cg.email.toLowerCase().includes(q)) ||
        (cg.caregiver_id && cg.caregiver_id.toLowerCase().includes(q)) ||
        (row.caregiver_id && row.caregiver_id.toLowerCase().includes(q))
      );
    });
  }, [rows, search, statusFilter]);

  // Header count always equals the number of rows currently shown so it never
  // contradicts the visible list under any search/status filter (FECOA-1578).
  const assignedCount = filteredRows.length;

  // setSearchParams navigates to "?<params>", which drops the #caregivers hash.
  // ParticipantDetails picks its tab from the hash (FECOA-1626), so losing it
  // bounced the SC back to the Info tab instead of opening the caregiver.
  const setSelectedCaregiver = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('selected_caregiver', id);
    else next.delete('selected_caregiver');
    navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash });
  };

  const goToDetail = (row) => {
    const id = row.caregiver?.id || row.caregiver_id;
    if (!id) return;
    setSelectedCaregiver(id);
  };

  const clearDetail = () => {
    setSelectedCaregiver(null);
    // Re-load list once the SC returns — counts/status may have changed via edit/reset/deactivate
    loadCaregivers();
  };

  if (!allowCaregivers && !loading) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-12">
        Caregiver support is not enabled for this study. Enable it from the Study Details &gt; Protocol Settings.
      </div>
    );
  }

  // Inline caregiver detail (no route change). Selected via ?selected_caregiver=<id>.
  if (selectedCaregiverId) {
    return (
      <CaregiverDetail
        caregiverID={selectedCaregiverId}
        participantID={participantId}
        studyCode={studyCode}
        onBack={clearDetail}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Assigned Caregivers ({assignedCount})
        </h3>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Caregiver
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, ID, or email..."
            className="w-full pl-10 pr-3 py-2 bg-white dark:bg-slate-800/60 border border-gray-300 dark:border-slate-700 rounded-md text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="relative z-50">
          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800/60 text-sm text-gray-700 dark:text-white"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter: {statusFilter}
          </button>
          {filterOpen && (
            <div className="absolute right-0 mt-1 w-52 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg z-[100] py-1">
              {FILTER_OPTIONS.map((opt) => {
                const selected = statusFilter === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      setStatusFilter(opt);
                      setFilterOpen(false);
                    }}
                    className={`flex items-center justify-between w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 ${
                      selected
                        ? 'text-primary-700 dark:text-primary-300 font-medium bg-primary-50 dark:bg-primary-500/10'
                        : 'text-gray-800 dark:text-gray-100'
                    }`}
                  >
                    <span>{opt}</span>
                    {selected && <Check className="w-4 h-4 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-gray-500 dark:text-gray-400" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-12">
          Please click the &lsquo;+ Add Caregiver&rsquo; button to add Caregivers for this Participant.
        </p>
      ) : filteredRows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-12">
          No caregivers found matching your criteria.
        </p>
      ) : (
        <div className="space-y-3">
          {filteredRows.map((row) => {
            const cg = row.caregiver || {};
            return (
              <div
                key={row.link_id}
                onClick={() => goToDetail(row)}
                className="grid grid-cols-12 gap-4 items-center px-4 py-3 bg-white dark:bg-slate-800/40 hover:bg-gray-50 dark:hover:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md cursor-pointer"
              >
                <div className="col-span-3">
                  <div className="text-xs uppercase text-gray-500 dark:text-gray-500">Name</div>
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {cg.full_name || cg.first_name
                      ? `${cg.first_name || ''} ${cg.last_name || ''}`.trim()
                      : '—'}
                  </div>
                  <div className="mt-1">
                    <CaregiverStatusBadge status={row.status} />
                  </div>
                  {row.status === 'Deactivated' && row.deactivated_at && (
                    <div className="text-xs text-gray-500 mt-1">
                      on {formatShortDate(row.deactivated_at)}
                    </div>
                  )}
                </div>
                <div className="col-span-3">
                  <div className="text-xs uppercase text-gray-500 dark:text-gray-500">Caregiver ID</div>
                  <div className="text-sm text-gray-900 dark:text-white font-medium">{row.caregiver_id}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Relationship: {row.relationship_type || '—'}
                  </div>
                </div>
                <div className="col-span-5">
                  <div className="text-xs uppercase text-gray-500 dark:text-gray-500">Contact</div>
                  <div className="text-sm text-gray-900 dark:text-white">{cg.email || '—'}</div>
                  {cg.phone && <div className="text-xs text-gray-500 dark:text-gray-400">{cg.phone}</div>}
                </div>
                <div className="col-span-1 flex justify-end">
                  {row.is_active && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeactivateTarget(row);
                      }}
                      title="Deactivate Link"
                      className="p-2 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 rounded"
                    >
                      <Unlink className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {addOpen && (
        <AddCaregiverModal
          participantId={participantId}
          participantLabel={participantLabel}
          siteId={siteId}
          studyCode={studyCode}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            loadCaregivers();
            onCaregiversChanged?.();
          }}
        />
      )}

      {deactivateTarget && (
        <DeactivateLinkConfirm
          link={deactivateTarget}
          onClose={() => setDeactivateTarget(null)}
          onConfirmed={() => {
            setDeactivateTarget(null);
            loadCaregivers();
            onCaregiversChanged?.();
          }}
        />
      )}
    </div>
  );
};

export default CaregiversTab;
